/**
 * EL-197 — a PÁGINA do relatório comparativo (lote de 09/10/2026, item 7).
 *
 * Pedido literal: "Em cenários, crie um botão que me permite gerar um relatório trazendo os
 * cenários que eu selecionar e uma análise crítica comparativa deles."
 *
 * Desenhada SEM a casca (`main.ts` desvia para cá antes de criar a casca, como o convite):
 * imprime limpa — sem barra lateral nem a superior — e o endereço pode ser copiado. Na tela
 * segue o tema; na impressão (A4 deitado) força o tema CLARO (estilo.css, seção 26).
 *
 * O QUE ELA MOSTRA é o que `modelo.ts` + `analise.ts` (puros, testados) devolvem — aqui só
 * se desenha. Todo número desenhado carrega `data-campo`/`data-valor`/`data-cen`: é por eles
 * que `tools/dev/validar-relatorio.mjs` confere cada valor contra a tela de cada cenário
 * aberto individualmente. Todos os perfis geram o MESMO conteúdo: sem memória de cálculo e
 * sem etiqueta EL (a do "Mostrar IDs" do Admin Hype some na impressão).
 *
 * O ENDEREÇO É EDITÁVEL: o que a tela garante (2 a 5 cenários publicados do mesmo
 * empreendimento) é conferido de novo aqui, contra o que o banco devolve.
 */
import { h } from '../dom';
import { icone } from '../icones';
import { ehDemonstracao } from '../config';
import { aplicarIdsDoPerfil, marcarIds } from '../ui/ids';
import { analisar, quemLimitouComoPartes, textoDoDesempate } from './analise';
import type { Analise } from './analise';
import { formatar } from './frase';
import type { CampoValor, Formato, Frase, Parte } from './frase';
import { conferirCenariosDoRelatorio, lerPedidoDoRelatorio } from './endereco';
import { montarModelo } from './modelo';
import type { ModeloCenario, ModeloRelatorio } from './modelo';
import { CRIT_NOMES } from './textos';
import { abrirCenario, listarEmpreendimentos, listarEmpresas } from '../../../../packages/dados/src/repositorios';
import type { CenarioAberto } from '../../../../packages/dados/src/repositorios';
import { cenariosComRascunho } from '../../../../packages/dados/src/rascunho';
import { podeEditar } from '../../../../packages/dados/src/sessao';
import type { Perfil } from '../../../../packages/dados/src/sessao';

/* ============================================================================
 * Ler, conferir, montar
 * ========================================================================== */

/** "09/10/2026 às 14:32" — data e hora absolutas, no relógio de quem gerou. */
function dataHora(d: Date): string {
  return `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

export async function telaRelatorio(perfil: Perfil): Promise<HTMLElement> {
  document.documentElement.classList.add('pg-relatorio');
  aplicarIdsDoPerfil(perfil.isAdminHype);
  document.title = 'Relatório comparativo — Fincast';

  /* A página de erro também é EL-197, e `marcarIds` olha os DESCENDENTES: embrulhada, ela ganha a
     etiqueta no "Mostrar IDs" do Admin Hype, como a página de sucesso (ver o fim da função). */
  const erroPg = (mensagem: string, detalhe?: string): HTMLElement => {
    const p = paginaDeErro(mensagem, detalhe);
    if (perfil.isAdminHype) marcarIds(h('div', {}, p));
    return p;
  };

  const pedido = lerPedidoDoRelatorio(window.location.search);
  if (!pedido.ok) return erroPg(pedido.erro);

  let abertos: CenarioAberto[];
  try {
    abertos = await Promise.all(pedido.ids.map((id) => abrirCenario(id)));
  } catch (e) {
    return erroPg('Um dos cenários deste relatório não pôde ser aberto: ou ele não existe mais, ou você não tem acesso a ele.',
      e instanceof Error ? e.message : String(e));
  }
  const erro = conferirCenariosDoRelatorio(abertos.map((a) => a.cenario));
  if (erro !== null) return erroPg(erro);

  const c0 = abertos[0]!.cenario;
  const [empresas, empreendimentos] = await Promise.all([
    listarEmpresas().catch(() => []),
    listarEmpreendimentos(c0.empresaId).catch(() => []),
  ]);
  let meus = new Set<string>();
  /* ADM-2: a situação lida AGORA (esta lista) vale sobre o perfil em memória; fora dela (a lista
     falhou: `[]`), decide o perfil, como antes. */
  if (podeEditar(perfil, c0.empresaId, empresas.find((e) => e.id === c0.empresaId)?.ativa)) {
    try {
      meus = await cenariosComRascunho(abertos.map((a) => ({ id: a.cenario.id, versao: a.cenario.versao })));
    } catch {
      /* sem a leitura do rascunho o relatório sai igual — só sem o aviso */
    }
  }
  const nomeEmp = empreendimentos.find((e) => e.id === c0.empreendimentoId)?.nome ?? 'Empreendimento';
  const modelo = montarModelo(
    {
      incorporadora: empresas.find((e) => e.id === c0.empresaId)?.nome ?? 'Incorporadora',
      empreendimento: nomeEmp,
      geradoPor: perfil.nome !== '' ? perfil.nome : perfil.email,
      geradoEm: dataHora(new Date()),
      objetivo: pedido.objetivo,
    },
    abertos.map((a) => ({
      id: a.cenario.id,
      nome: a.cenario.nome,
      versaoPublicada: a.cenario.versaoPublicada,
      empresaId: a.cenario.empresaId,
      empreendimentoId: a.cenario.empreendimentoId,
      entrada: a.entrada,
      temRascunhoMeu: meus.has(a.cenario.id),
    })),
  );
  document.title = `Relatório comparativo — ${nomeEmp} — Fincast`;
  const pagina = desenhar(modelo, analisar(modelo));
  /* `marcarIds` olha os DESCENDENTES: embrulhado, a própria página (EL-197) ganha a etiqueta. */
  if (perfil.isAdminHype) marcarIds(h('div', {}, pagina));
  return pagina;
}

function paginaDeErro(mensagem: string, detalhe?: string): HTMLElement {
  return h('div', { class: 'rel rel-erro-pg', 'data-el': 'EL-197', 'data-relatorio-erro': '1' },
    cabecalho(false),
    h('div', { class: 'card rel-erro', role: 'alert' },
      h('span', { class: 'rel-erro-ic', html: icone.alerta(26) }),
      h('h2', {}, 'Não foi possível montar o relatório'),
      h('p', {}, mensagem),
      detalhe !== undefined && detalhe !== mensagem ? h('p', { class: 'muted' }, detalhe) : null,
      h('p', { class: 'muted' }, 'Volte ao cenário e gere o relatório de novo pelo botão "Relatório comparativo".')));
}

/* ============================================================================
 * Peças
 * ========================================================================== */

function cabecalho(comAcoes: boolean): HTMLElement {
  return h('header', { class: 'rel-topo' },
    h('div', { class: 'brand rel-marca' }, h('span', { class: 'logo' }, 'F'), h('span', {}, 'Fincast')),
    comAcoes
      ? h('div', { class: 'rel-acoes' },
          h('button', { class: 'btn pri', type: 'button', 'data-acao': 'imprimir', onclick: () => window.print() },
            h('span', { html: icone.imprimir(15) }), 'Imprimir / Salvar em PDF'))
      : null);
}

/** Uma frase da análise: os números em negrito, cada um com o valor cru para conferência. */
function fraseEl(f: Frase, tag: 'span' | 'li' | 'p' = 'span', classe?: string): HTMLElement {
  const el = h(tag, classe !== undefined ? { class: classe } : {});
  for (const p of f.partes) el.appendChild(parteEl(p));
  return el;
}

function parteEl(p: Parte): Node {
  if (typeof p === 'string') return document.createTextNode(p);
  if ('num' in p) return h('b', { class: 'rel-n', 'data-valor': String(p.num.valor) }, formatar(p.num.valor, p.num.formato));
  return h('span', { class: 'rel-nome' }, p.nome);
}

function seloVeredito(c: ModeloCenario): HTMLElement {
  const tom = !c.suficiente ? 'out' : c.cor === 'green' ? 'ok' : c.cor === 'yellow' ? 'warn' : 'bad';
  return h('span', { class: `badge ${tom}`, 'data-cen': c.id, 'data-campo': 'veredito', 'data-valor': c.suficiente ? c.cor : 'insuficiente' }, c.veredito);
}

function secao(titulo: string, classe: string, ...filhos: (Node | null)[]): HTMLElement {
  return h('section', { class: `rel-sec ${classe}` }, h('h2', {}, titulo), ...filhos);
}

/* ---------------------------------------------------------------- tabelas lado a lado */

type Linha =
  | { tipo: 'sec'; titulo: string }
  | { tipo: 'num'; rotulo: string; campo: CampoValor; formato: Formato; nulo?: string }
  | { tipo: 'sn'; rotulo: string; campo: string; valor: (c: ModeloCenario) => boolean }
  | { tipo: 'txt'; rotulo: string; campo: string; valor: (c: ModeloCenario) => Node | string };

function tabela(m: ModeloRelatorio, linhas: Linha[], classe: string): HTMLElement {
  const n = m.cenarios.length;
  const cab = h('tr', {}, h('th', {}, ''), ...m.cenarios.map((c) =>
    h('th', { 'data-cen': c.id }, h('span', { class: 'rel-th-nm' }, c.nome),
      h('span', { class: 'rel-th-v' }, `publicado ${formatar(c.versaoPublicada, 'versao')}`))));
  const corpo = linhas.map((l) => {
    if (l.tipo === 'sec') return h('tr', { class: 'rel-tsec' }, h('td', { colspan: String(n + 1) }, l.titulo));
    const celulas = m.cenarios.map((c) => {
      if (l.tipo === 'num') {
        const v = c.valores[l.campo];
        return v === null
          ? h('td', { 'data-cen': c.id, 'data-campo': l.campo, 'data-valor': '' }, l.nulo ?? '—')
          : h('td', { 'data-cen': c.id, 'data-campo': l.campo, 'data-valor': String(v) }, formatar(v, l.formato));
      }
      if (l.tipo === 'sn') {
        const v = l.valor(c);
        return h('td', { 'data-cen': c.id, 'data-campo': l.campo, 'data-valor': v ? '1' : '0' }, v ? 'Sim' : 'Não');
      }
      return h('td', { 'data-cen': c.id, 'data-campo': l.campo }, l.valor(c));
    });
    return h('tr', {}, h('td', { class: 'rel-rot' }, l.rotulo), ...celulas);
  });
  return h('div', { class: 'rel-twrap' }, h('table', { class: `rel-tab ${classe}` }, h('thead', {}, cab), h('tbody', {}, ...corpo)));
}

const LINHAS_PREMISSAS: Linha[] = [
  { tipo: 'sn', rotulo: 'Tabela «Demanda por unidade» preenchida', campo: 'temTabela', valor: (c) => c.premissas.temTabela },
  { tipo: 'num', rotulo: 'Contratação do PJ', campo: 'ymContratacao', formato: 'mes' },
  { tipo: 'num', rotulo: 'Cumprimento de demanda', campo: 'ymDesejada', formato: 'mes' },
  { tipo: 'num', rotulo: 'Unidades totais', campo: 'unidades', formato: 'inteiro' },
  { tipo: 'num', rotulo: 'Vendidas na demanda', campo: 'vendidas', formato: 'inteiro' },
  { tipo: 'num', rotulo: 'Financiadas na demanda', campo: 'financiadas', formato: 'inteiro' },
  { tipo: 'num', rotulo: '% financiado do valor de venda', campo: 'pctFin', formato: 'taxa' },
  { tipo: 'num', rotulo: 'Obra como garantia', campo: 'obraGarPct', formato: 'taxa' },
  { tipo: 'num', rotulo: 'Mínimo de obra', campo: 'minObraPct', formato: 'taxa' },
  { tipo: 'num', rotulo: 'Aporte', campo: 'aporte', formato: 'moeda' },
  { tipo: 'sn', rotulo: 'Terreno como garantia', campo: 'terrenoGarantia', valor: (c) => c.premissas.terrenoGarantia },
  { tipo: 'num', rotulo: 'Valor de avaliação do terreno', campo: 'valorTerreno', formato: 'moeda' },
  { tipo: 'num', rotulo: 'Taxa de consumo de LGC', campo: 'lgcPct', formato: 'taxa' },
  { tipo: 'num', rotulo: 'LGC disponível', campo: 'lgcDisponivel', formato: 'moeda' },
  { tipo: 'sn', rotulo: 'PJ limitado ao LGC disponível', campo: 'pjLimitadoLGC', valor: (c) => c.premissas.pjLimitadoLGC },
  { tipo: 'num', rotulo: 'PJ limite', campo: 'pjLimite', formato: 'moeda', nulo: 'Sem limite' },
  { tipo: 'txt', rotulo: 'Funding', campo: 'funding', valor: (c) => c.premissas.fundingObra },
  { tipo: 'sn', rotulo: 'EVV', campo: 'temEVV', valor: (c) => c.premissas.temEVV },
];

const LINHAS_RESULTADOS: Linha[] = [
  { tipo: 'num', rotulo: 'VGV', campo: 'VGV', formato: 'moeda' },
  { tipo: 'num', rotulo: 'CTO MCV', campo: 'CTO', formato: 'moeda' },
  { tipo: 'sec', titulo: 'Composição das garantias' },
  { tipo: 'num', rotulo: 'FSF', campo: 'FSF', formato: 'moeda' },
  { tipo: 'num', rotulo: 'Terreno', campo: 'terreno', formato: 'moeda' },
  { tipo: 'num', rotulo: 'Obra', campo: 'obra', formato: 'moeda' },
  { tipo: 'num', rotulo: 'Aporte', campo: 'aporte', formato: 'moeda' },
  { tipo: 'num', rotulo: 'Total das garantias', campo: 'garantias', formato: 'moeda' },
  { tipo: 'sec', titulo: 'Empréstimo PJ' },
  { tipo: 'num', rotulo: 'PJ a contratar', campo: 'PJ', formato: 'moeda' },
  { tipo: 'txt', rotulo: 'Quem limitou o PJ', campo: 'quemLimitou', valor: (c) => fraseEl({ partes: quemLimitouComoPartes(c) }) },
  { tipo: 'num', rotulo: 'PJ limite', campo: 'pjLimite', formato: 'moeda', nulo: 'Sem limite' },
  { tipo: 'num', rotulo: 'Garantias + PJ', campo: 'total', formato: 'moeda' },
  { tipo: 'num', rotulo: 'Descoberto (saldo para atingir a demanda)', campo: 'gapCTO', formato: 'moeda' },
  { tipo: 'num', rotulo: 'Cobertura do CTO', campo: 'cobertura', formato: 'fracao' },
  { tipo: 'sec', titulo: 'Linha de crédito (LGC)' },
  { tipo: 'num', rotulo: 'LGC consumido', campo: 'tomadaLGC', formato: 'moeda' },
  { tipo: 'num', rotulo: 'Saldo de LGC', campo: 'saldoLGC', formato: 'moeda' },
  { tipo: 'sec', titulo: 'IGH estático' },
  { tipo: 'num', rotulo: 'Garantia em estoque', campo: 'ighReal', formato: 'moeda' },
  { tipo: 'num', rotulo: 'Mínimo exigido (1,30 × PJ)', campo: 'ighMin', formato: 'moeda' },
  { tipo: 'sec', titulo: 'Demanda' },
  { tipo: 'num', rotulo: 'Vendidas na demanda', campo: 'vendidas', formato: 'inteiro' },
  { tipo: 'num', rotulo: 'Mínimo de vendidas', campo: 'vendExig', formato: 'inteiro' },
  { tipo: 'num', rotulo: 'Financiadas na demanda', campo: 'financiadas', formato: 'inteiro' },
  { tipo: 'num', rotulo: 'Mínimo de financiadas', campo: 'finExig', formato: 'inteiro' },
  { tipo: 'sec', titulo: 'Prazo' },
  { tipo: 'num', rotulo: 'Cumprimento de demanda', campo: 'ymDesejada', formato: 'mes', nulo: 'Sem data' },
  { tipo: 'num', rotulo: 'Prazo normativo', campo: 'ymNorm', formato: 'mes', nulo: 'Sem data' },
];

/** A folga de cada critério, onde ela existe (garantia não tem: o PJ fecha a conta). */
const FOLGA_DO_CRITERIO: ReadonlyArray<[CampoValor, Formato] | null> = [
  null, ['folgaVend', 'folga'], ['folgaFin', 'folga'], ['folgaLGC', 'folga'], ['folgaIGH', 'folga'], ['folgaObra', 'pp'], ['folgaPrazo', 'meses'],
];

function tabelaCriterios(m: ModeloRelatorio): HTMLElement {
  const linhas: Linha[] = CRIT_NOMES.map((nome, i) => ({
    tipo: 'txt' as const,
    rotulo: nome,
    campo: `crit-${i}`,
    valor: (c: ModeloCenario): Node => {
      if (!c.suficiente) return document.createTextNode('—');
      const cr = c.criterios[i]!;
      const marca = h('span', { class: cr.ok ? 'cok' : 'cno', 'data-cen': c.id, 'data-campo': `crit-${i}`, 'data-valor': cr.ok ? '1' : '0' },
        cr.ok ? '✓' : '✗');
      const f = FOLGA_DO_CRITERIO[i];
      let det: Node | null = null;
      if (f === null || f === undefined) {
        const gap = c.valores.gapCTO ?? 0;
        det = gap > 0.5 ? h('span', { class: 'rel-folga' }, 'descoberto ', h('b', { class: 'rel-n', 'data-valor': String(gap) }, formatar(gap, 'moeda'))) : null;
      } else {
        const v = c.valores[f[0]];
        det = v === null
          ? h('span', { class: 'rel-folga muted' }, 'sem data')
          : h('span', { class: 'rel-folga' }, 'folga ', h('b', { class: 'rel-n', 'data-cen': c.id, 'data-campo': f[0], 'data-valor': String(v) }, formatar(v, f[1])));
      }
      return h('span', { class: 'rel-crit' }, marca, det);
    },
  }));
  linhas.push({ tipo: 'num', rotulo: 'Critérios atendidos (de 7)', campo: 'nCriterios', formato: 'inteiro' });
  linhas.push({ tipo: 'txt', rotulo: 'Veredito', campo: 'cor', valor: seloVeredito });
  return tabela(m, linhas, 'rel-tab-crit');
}

/* ---------------------------------------------------------------- gráfico */

const SERIES: ReadonlyArray<{ campo: CampoValor; nome: string; classe: string }> = [
  { campo: 'FSF', nome: 'FSF', classe: 'fsf' },
  { campo: 'terreno', nome: 'Terreno', classe: 'terr' },
  { campo: 'aporte', nome: 'Aporte', classe: 'apo' },
  { campo: 'obra', nome: 'Obra', classe: 'obra' },
  { campo: 'PJ', nome: 'PJ', classe: 'pj' },
];

const mi = (v: number): string => `R$ ${(v / 1e6).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`;

/** Passo "redondo" do eixo: 1, 2 ou 5 × 10^k, com 4 a 6 linhas. */
function passoDoEixo(max: number): number {
  const bruto = max / 5;
  const pot = 10 ** Math.floor(Math.log10(bruto || 1));
  const r = bruto / pot;
  return (r <= 1 ? 1 : r <= 2 ? 2 : r <= 5 ? 5 : 10) * pot;
}

function grafico(m: ModeloRelatorio): HTMLElement {
  const NS = 'http://www.w3.org/2000/svg';
  const el = (tag: string, at: Record<string, string | number>, ...filhos: (Node | string)[]): SVGElement => {
    const e = document.createElementNS(NS, tag);
    for (const [k, v] of Object.entries(at)) e.setAttribute(k, String(v));
    for (const f of filhos) e.appendChild(typeof f === 'string' ? document.createTextNode(f) : f);
    return e;
  };
  const cs = m.cenarios;
  /* Largura lógica FIXA (960): mostrado a 100% no relatório, o texto do gráfico fica no
     tamanho da página — com uma caixa estreita, o navegador ampliaria rótulos e números. */
  const W = 960, esq = 78, topo = 18, alt = 250, baixo = 46;
  const slot = (W - esq - 10) / Math.max(1, cs.length);
  const larguraBarra = Math.min(96, slot * 0.42);
  const H = topo + alt + baixo;
  const maxV = Math.max(1, ...cs.map((c) => Math.max(c.valores.CTO ?? 0, (c.valores.total ?? 0) + (c.valores.gapCTO ?? 0)))) * 1.08;
  const passo = passoDoEixo(maxV);
  const y = (v: number) => topo + alt - (Math.max(0, v) / maxV) * alt;
  const svg = el('svg', { viewBox: `0 0 ${W} ${H}`, class: 'rel-graf', role: 'img', 'aria-label': 'Composição das garantias e do PJ de cada cenário, comparada ao CTO' });
  for (let v = 0; v <= maxV; v += passo) {
    svg.appendChild(el('line', { x1: esq, x2: W - 6, y1: y(v), y2: y(v), class: 'rel-g-grade' }));
    svg.appendChild(el('text', { x: esq - 8, y: y(v) + 4, 'text-anchor': 'end', class: 'rel-g-eixo' }, v === 0 ? '0' : mi(v)));
  }
  cs.forEach((c, i) => {
    const x = esq + i * slot + (slot - larguraBarra) / 2;
    const g = el('g', { 'data-cen': c.id });
    const clipId = `relc-${i}`;
    const totalBarra = (c.valores.total ?? 0) + (c.valores.gapCTO ?? 0);
    g.appendChild(el('clipPath', { id: clipId }, el('rect', { x, y: y(totalBarra), width: larguraBarra, height: Math.max(0, y(0) - y(totalBarra)), rx: 4 })));
    const pilha = el('g', { 'clip-path': `url(#${clipId})` });
    let acum = 0;
    const segs: Array<{ v: number; classe: string; nome: string }> = [
      ...SERIES.map((s) => ({ v: c.valores[s.campo] ?? 0, classe: s.classe, nome: s.nome })),
      { v: c.valores.gapCTO ?? 0, classe: 'desc', nome: 'Descoberto' },
    ];
    for (const s of segs) {
      if (s.v <= 0.5) continue;
      const y0 = y(acum + s.v), y1 = y(acum);
      /* 2px de vão entre as parcelas (a superfície aparece entre elas) */
      const hh = Math.max(0.5, y1 - y0 - (acum > 0 ? 2 : 0));
      pilha.appendChild(el('rect', { x, y: y0, width: larguraBarra, height: hh, class: `rel-seg ${s.classe}` },
        el('title', {}, `${c.nome} — ${s.nome}: ${formatar(s.v, 'moeda')}`)));
      acum += s.v;
    }
    g.appendChild(pilha);
    const cto = c.valores.CTO ?? 0;
    g.appendChild(el('line', { x1: x - 8, x2: x + larguraBarra + 8, y1: y(cto), y2: y(cto), class: 'rel-g-cto' },
      el('title', {}, `${c.nome} — CTO: ${formatar(cto, 'moeda')}`)));
    g.appendChild(el('text', { x: x + larguraBarra / 2, y: y(Math.max(totalBarra, cto)) - 6, 'text-anchor': 'middle', class: 'rel-g-val' },
      mi(c.valores.total ?? 0)));
    const rot = c.nome.length > 24 ? c.nome.slice(0, 23) + '…' : c.nome;
    g.appendChild(el('text', { x: x + larguraBarra / 2, y: topo + alt + 18, 'text-anchor': 'middle', class: 'rel-g-nome' }, rot, el('title', {}, c.nome)));
    g.appendChild(el('text', { x: x + larguraBarra / 2, y: topo + alt + 34, 'text-anchor': 'middle', class: 'rel-g-sub' }, c.veredito));
    svg.appendChild(g);
  });
  const legenda = h('ul', { class: 'rel-leg' },
    ...SERIES.map((s) => h('li', {}, h('span', { class: `rel-sw ${s.classe}` }), s.nome)),
    h('li', {}, h('span', { class: 'rel-sw desc' }), 'Descoberto'),
    h('li', {}, h('span', { class: 'rel-sw-cto' }), 'CTO exigido'));
  return h('div', { class: 'rel-graf-box' }, svg as unknown as HTMLElement, legenda);
}

/* ---------------------------------------------------------------- análise */

function ranking(m: ModeloRelatorio, a: Analise): HTMLElement {
  const porId = new Map(m.cenarios.map((c) => [c.id, c]));
  const fora = m.cenarios.filter((c) => !c.suficiente);
  return h('div', {},
    h('p', { class: 'rel-lead' }, `Objetivo: «${a.objetivo.t}». Métrica: ${a.regra.rotulo} (${a.regra.sentido === 'max' ? 'quanto maior, melhor' : 'quanto menor, melhor'}). A viabilidade vem antes do objetivo.`),
    a.ranking.length === 0
      ? h('p', { class: 'muted' }, 'Nenhum cenário com dados suficientes para ordenar.')
      : h('ol', { class: 'rel-rank' }, ...a.ranking.map((p) => {
        const c = porId.get(p.id)!;
        return h('li', { 'data-cen': p.id, 'data-posicao': String(p.posicao), class: p.id === a.recomendadoId ? 'rec' : '' },
          h('span', { class: 'rel-rank-pos' }, String(p.posicao)),
          h('span', { class: 'rel-rank-tx' },
            h('span', { class: 'rel-rank-nm' }, p.nome, ' ', seloVeredito(c)),
            h('span', { class: 'rel-rank-met' }, `${a.regra.rotulo}: `, parteEl(p.metrica),
              p.decidido !== null ? ` · ${textoDoDesempate(p.decidido)}` : '')));
      })),
    fora.length > 0
      ? h('p', { class: 'muted' }, `Fora do ranking (dados insuficientes): ${fora.map((c) => c.nome).join(', ')}.`)
      : null);
}

function porCenario(itens: Array<{ id: string; nome: string; conteudo: Node }>): HTMLElement {
  return h('div', { class: 'rel-blocos' }, ...itens.map((i) => h('div', { class: 'rel-bloco', 'data-cen': i.id }, h('h3', {}, i.nome), i.conteudo)));
}

/* ============================================================================
 * A página
 * ========================================================================== */

function desenhar(m: ModeloRelatorio, a: Analise): HTMLElement {
  const rec = m.cenarios.find((c) => c.id === a.recomendadoId) ?? null;

  const ident = h('dl', { class: 'rel-ident' },
    h('dt', {}, 'Incorporadora'), h('dd', { 'data-campo': 'incorporadora' }, m.incorporadora),
    h('dt', {}, 'Empreendimento'), h('dd', { 'data-campo': 'empreendimento' }, m.empreendimento),
    h('dt', {}, 'Cenários'), h('dd', {}, h('ul', { class: 'rel-cens' }, ...m.cenarios.map((c) =>
      h('li', { 'data-cen': c.id }, h('span', { class: 'rel-nome' }, c.nome), ' ',
        h('span', { class: 'badge out', 'data-campo': 'versao', 'data-cen': c.id, 'data-valor': String(c.versaoPublicada) },
          `publicado ${formatar(c.versaoPublicada, 'versao')}`))))),
    h('dt', {}, 'Objetivo'), h('dd', { 'data-campo': 'objetivo', 'data-valor': m.objetivo.k }, h('b', {}, m.objetivo.t), h('span', { class: 'muted' }, ` — ${m.objetivo.d}`)),
    h('dt', {}, 'Gerado por'), h('dd', { 'data-campo': 'geradoPor' }, m.geradoPor),
    h('dt', {}, 'Gerado em'), h('dd', { 'data-campo': 'geradoEm' }, m.geradoEm));

  const avisos = a.avisos.length > 0
    ? h('div', { class: 'rel-aviso', role: 'note', 'data-aviso': 'rascunho' }, h('span', { html: icone.alerta(16) }),
        h('div', {}, ...a.avisos.map((f) => fraseEl(f, 'p'))))
    : null;

  const capa = h('section', { class: 'rel-sec rel-capa' },
    h('p', { class: 'rel-kicker' }, 'Relatório comparativo de cenários'),
    h('h1', {}, m.empreendimento),
    ident,
    ehDemonstracao() ? h('p', { class: 'rel-demo' }, 'Gerado no ambiente de demonstração: os dados são de teste.') : null,
    avisos);

  const resumo = secao('Resumo executivo', 'rel-resumo',
    h('div', { class: `rel-rec${rec === null ? ' sem' : ''}`, 'data-recomendado': rec?.id ?? '' },
      h('span', { class: 'rel-rec-ic', html: icone.alvo(20) }),
      h('div', {}, ...a.porque.map((f, i) => fraseEl(f, 'p', i === 0 ? 'rel-rec-tit' : undefined)))),
    h('ul', { class: 'rel-lista rel-resumo-lista' }, ...m.cenarios.map((c, i) =>
      h('li', { 'data-cen': c.id }, seloVeredito(c), ' ', fraseEl(a.resumo[i]!)))));

  const premissas = secao('Premissas-chave', 'rel-quebra', tabela(m, LINHAS_PREMISSAS, 'rel-tab-prem'));
  const resultados = secao('Resultados', 'rel-quebra', tabela(m, LINHAS_RESULTADOS, 'rel-tab-res'));
  const criterios = secao('Os 7 critérios', 'rel-quebra',
    h('p', { class: 'rel-lead' }, 'Folga onde ela existe: vendidas e financiadas contra o mínimo exigido, LGC contra o disponível, IGH contra o mínimo; obra em pontos percentuais; data em meses até o prazo normativo.'),
    tabelaCriterios(m));
  const graf = secao('Composição das garantias × CTO', '', grafico(m));
  const rank = secao('Ranking pelo objetivo', 'rel-quebra', ranking(m, a));
  const trocas = secao('O que se troca entre eles', '',
    a.tradeoffs.length > 0 ? h('ul', { class: 'rel-lista' }, ...a.tradeoffs.map((f) => fraseEl(f, 'li'))) : h('p', { class: 'muted' }, 'Só há um cenário com dados suficientes.'));
  const riscos = secao('Riscos', '', porCenario(a.riscos.map((r) => ({
    id: r.id, nome: r.nome,
    conteudo: r.frases.length > 0 ? h('ul', { class: 'rel-lista con' }, ...r.frases.map((f) => fraseEl(f, 'li'))) : h('p', { class: 'muted' }, 'Nenhum risco destacado pelas regras.'),
  }))));
  const recomendacoes = secao('Recomendações', '', porCenario(a.recomendacoes.map((r) => ({
    id: r.id, nome: r.nome,
    conteudo: r.itens.length > 0
      ? h('ul', { class: 'rel-lista' }, ...r.itens.map((it) => h('li', {}, fraseEl(it.frase),
          it.alavancas.length > 0 ? h('div', { class: 'rel-alav' }, h('span', { class: 'muted' }, 'Como resolver: '), ...it.alavancas.map((t) => h('span', { class: 'chip' }, t))) : null)))
      : h('p', { class: 'muted' }, 'Todos os critérios cumpridos: nada a ajustar.'),
  }))));
  const notas = secao('Notas de método', 'rel-notas', h('ul', { class: 'rel-lista' }, ...a.notas.map((f) => fraseEl(f, 'li'))));

  const pe = h('footer', { class: 'rel-pe' }, `Fincast · relatório gerado por ${m.geradoPor} em ${m.geradoEm} · cada cenário na sua última versão publicada.`);

  /* Cabeçalho e rodapé de CADA página impressa (A4 deitado): caixas de margem do @page, com
     a data. O texto é do usuário (nome do empreendimento), então vai escapado para string CSS. */
  const css = (s: string) => `"${s.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/[\r\n]+/g, ' ')}"`;
  /* O @page mora AQUI, e não no estilo.css: ele não tem escopo (valeria para imprimir
     qualquer tela do app), e este <style> só existe na aba do relatório. */
  const caixa = 'font-family:InterVar,Inter,"Segoe UI",system-ui,sans-serif;font-size:8.5pt;color:#6b6b74;';
  const estiloImpressao = h('style', {}, `@media print{@page{size:A4 landscape;margin:15mm 12mm 14mm;`
    + `@top-left{content:${css(`Fincast · Relatório comparativo — ${m.empreendimento}`)};${caixa}}`
    + `@top-right{content:${css(`Gerado em ${m.geradoEm}`)};${caixa}}`
    + `@bottom-left{content:${css(`Gerado por ${m.geradoPor} · ${m.incorporadora}`)};${caixa}}`
    + `@bottom-right{content:"Página " counter(page) " de " counter(pages);${caixa}}}}`);

  return h('div', { class: 'rel', 'data-el': 'EL-197', 'data-relatorio-pronto': '1' },
    estiloImpressao, cabecalho(true), capa, resumo, premissas, resultados, criterios, graf, rank, trocas, riscos, recomendacoes, notas, pe);
}
